import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Download, LogOut, RefreshCw, Users, Mail, CalendarClock, TrendingUp, Search, Send } from "lucide-react";
import { adminApi, useAuth } from "../lib/auth";
import { Seo } from "../components/Seo";

const TABS = [
  { key: "subscribers", label: "Subscribers", endpoint: "/admin/subscribers", icon: Mail,
    cols: [["email", "Email"], ["created_at", "Subscribed"]] },
  { key: "leads", label: "Contact Leads", endpoint: "/admin/leads", icon: Users,
    cols: [["name", "Name"], ["email", "Email"], ["phone", "Phone"], ["source", "Source"], ["message", "Message"], ["status", "Status"], ["created_at", "Date"]] },
  { key: "appointments", label: "Appointments", endpoint: "/admin/appointments", icon: CalendarClock,
    cols: [["name", "Name"], ["email", "Email"], ["phone", "Phone"], ["date", "Date"], ["time", "Time"], ["topic", "Topic"], ["created_at", "Booked"]] },
  { key: "rental", label: "Rental Analyses", endpoint: "/admin/rental-analyses", icon: TrendingUp,
    cols: [["name", "Name"], ["email", "Email"], ["address", "Address"], ["city", "City"], ["estimate", "Estimate"], ["created_at", "Date"]] },
];

const fmt = (v) => {
  if (v == null || v === "") return "—";
  if (typeof v === "string" && v.includes("T") && v.includes(":")) {
    const d = new Date(v);
    if (!isNaN(d)) return d.toLocaleString("en-US", { dateStyle: "medium", timeStyle: "short" });
  }
  return String(v);
};

const toCSV = (rows, cols) => {
  const header = cols.map(([, label]) => `"${label}"`).join(",");
  const body = rows.map((r) => cols.map(([k]) => `"${String(r[k] ?? "").replace(/"/g, '""')}"`).join(",")).join("\n");
  return `${header}\n${body}`;
};

export default function Admin() {
  const { admin, logout } = useAuth();
  const navigate = useNavigate();
  const [active, setActive] = useState("subscribers");
  const [data, setData] = useState({});
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [digesting, setDigesting] = useState(false);

  const tab = TABS.find((t) => t.key === active);

  const loadStats = useCallback(async () => {
    try { const { data } = await adminApi.get("/admin/stats"); setStats(data); } catch { /* ignore */ }
  }, []);

  const loadTab = useCallback(async (t) => {
    setLoading(true);
    try {
      const { data: rows } = await adminApi.get(t.endpoint);
      setData((prev) => ({ ...prev, [t.key]: rows }));
    } catch (err) {
      if (err?.response?.status === 401) { logout(); navigate("/login"); }
      else toast.error("Failed to load data.");
    } finally { setLoading(false); }
  }, [logout, navigate]);

  useEffect(() => { loadStats(); }, [loadStats]);
  useEffect(() => { if (tab) loadTab(tab); /* eslint-disable-next-line */ }, [active]);
  // Clear filters when switching tabs
  useEffect(() => { setSearch(""); setDateFrom(""); setDateTo(""); }, [active]);

  const allRows = data[active] || [];
  const rows = allRows.filter((r) => {
    if (search) {
      const hay = tab.cols.map(([k]) => String(r[k] ?? "")).join(" ").toLowerCase();
      if (!hay.includes(search.toLowerCase())) return false;
    }
    if (dateFrom || dateTo) {
      const created = r.created_at ? r.created_at.slice(0, 10) : "";
      if (dateFrom && created < dateFrom) return false;
      if (dateTo && created > dateTo) return false;
    }
    return true;
  });

  const updateStatus = async (leadId, status) => {
    try {
      await adminApi.patch(`/admin/leads/${leadId}/status`, { status });
      setData((prev) => ({
        ...prev,
        leads: (prev.leads || []).map((l) => (l.id === leadId ? { ...l, status } : l)),
      }));
      toast.success("Status updated.");
    } catch { toast.error("Could not update status."); }
  };

  const sendDigest = async () => {
    setDigesting(true);
    try {
      const { data } = await adminApi.post("/admin/send-digest");
      toast.success(`Digest emailed — ${data.leads} leads, ${data.appointments} appts, ${data.subscribers} subs (last 24h).`);
    } catch { toast.error("Could not send digest."); }
    finally { setDigesting(false); }
  };

  const exportCSV = () => {
    if (!rows.length) { toast.error("Nothing to export."); return; }
    const blob = new Blob([toCSV(rows, tab.cols)], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${active}-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success("CSV downloaded.");
  };

  const statMap = { subscribers: stats?.subscribers, leads: stats?.leads, appointments: stats?.appointments, rental: stats?.rental_analyses };

  return (
    <div className="grain min-h-screen bg-background">
      <Seo title="Admin Dashboard" description="Cummings & Co. admin dashboard." />
      {/* Header */}
      <header className="border-b border-border bg-primary text-primary-foreground">
        <div className="mx-auto flex max-w-[1400px] items-center justify-between px-6 py-5 lg:px-10">
          <div className="flex items-center gap-3">
            <span className="flex h-10 w-10 items-center justify-center bg-accent font-serif text-xl text-accent-foreground">C</span>
            <div>
              <div className="font-serif text-xl leading-none">Admin Dashboard</div>
              <div className="text-xs text-primary-foreground/50">{admin?.email}</div>
            </div>
          </div>
          <button onClick={() => { logout(); navigate("/login"); }} data-testid="logout-btn"
            className="flex items-center gap-2 border border-primary-foreground/30 px-4 py-2 text-sm transition-colors hover:bg-primary-foreground hover:text-primary">
            <LogOut className="h-4 w-4" /> Logout
          </button>
        </div>
      </header>

      <div className="mx-auto max-w-[1400px] px-6 py-10 lg:px-10">
        {/* Stat cards */}
        <div className="grid grid-cols-2 gap-px border border-border bg-border lg:grid-cols-4">
          {TABS.map((t) => (
            <button key={t.key} onClick={() => setActive(t.key)} data-testid={`stat-${t.key}`}
              className={`flex flex-col items-start bg-background p-6 text-left transition-colors hover:bg-muted ${active === t.key ? "ring-2 ring-inset ring-accent" : ""}`}>
              <t.icon className="h-5 w-5 text-accent" strokeWidth={1.5} />
              <span className="mt-4 font-serif text-4xl font-light">{statMap[t.key] ?? "—"}</span>
              <span className="mt-1 text-xs uppercase tracking-widest text-muted-foreground">{t.label}</span>
            </button>
          ))}
        </div>

        {/* Toolbar */}
        <div className="mt-10 flex flex-col gap-4 border-b border-border pb-5 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex flex-wrap gap-2">
            {TABS.map((t) => (
              <button key={t.key} onClick={() => setActive(t.key)} data-testid={`tab-${t.key}`}
                className={`px-4 py-2 text-sm font-medium transition-colors ${active === t.key ? "bg-primary text-primary-foreground" : "border border-border text-muted-foreground hover:text-foreground"}`}>
                {t.label}
              </button>
            ))}
          </div>
          <div className="flex flex-wrap gap-2">
            <button onClick={sendDigest} disabled={digesting} data-testid="send-digest-btn"
              className="flex items-center gap-2 border border-border px-4 py-2 text-sm hover:bg-muted disabled:opacity-60">
              <Send className="h-4 w-4" /> {digesting ? "Sending…" : "Email Digest"}
            </button>
            <button onClick={() => { loadTab(tab); loadStats(); }} data-testid="refresh-btn"
              className="flex items-center gap-2 border border-border px-4 py-2 text-sm hover:bg-muted">
              <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} /> Refresh
            </button>
            <button onClick={exportCSV} data-testid="export-csv-btn"
              className="flex items-center gap-2 bg-accent px-4 py-2 text-sm font-medium text-accent-foreground transition-opacity hover:opacity-90">
              <Download className="h-4 w-4" /> Export CSV
            </button>
          </div>
        </div>

        {/* Filters */}
        <div className="mt-5 flex flex-col gap-3 sm:flex-row sm:items-center">
          <div className="relative flex-1">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <input data-testid="admin-search" value={search} onChange={(e) => setSearch(e.target.value)}
              placeholder={`Search ${tab.label.toLowerCase()}…`}
              className="w-full border border-border bg-background py-2.5 pl-9 pr-4 text-sm focus:border-accent focus:outline-none" />
          </div>
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <span className="whitespace-nowrap text-xs uppercase tracking-wider">From</span>
            <input type="date" data-testid="admin-date-from" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)}
              className="border border-border bg-background px-3 py-2.5 text-sm text-foreground focus:border-accent focus:outline-none" />
            <span className="whitespace-nowrap text-xs uppercase tracking-wider">To</span>
            <input type="date" data-testid="admin-date-to" value={dateTo} onChange={(e) => setDateTo(e.target.value)}
              className="border border-border bg-background px-3 py-2.5 text-sm text-foreground focus:border-accent focus:outline-none" />
            {(search || dateFrom || dateTo) && (
              <button onClick={() => { setSearch(""); setDateFrom(""); setDateTo(""); }} data-testid="clear-filters"
                className="whitespace-nowrap text-accent hover:underline">Clear</button>
            )}
          </div>
        </div>

        {/* Table */}
        <div className="mt-6 overflow-x-auto border border-border">
          <table className="w-full min-w-[640px] text-left text-sm" data-testid="admin-table">
            <thead className="border-b border-border bg-muted">
              <tr>
                {tab.cols.map(([k, label]) => (
                  <th key={k} className="whitespace-nowrap px-4 py-3 text-xs uppercase tracking-wider text-muted-foreground">{label}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={tab.cols.length} className="px-4 py-10 text-center text-muted-foreground">Loading…</td></tr>
              ) : rows.length === 0 ? (
                <tr><td colSpan={tab.cols.length} className="px-4 py-10 text-center text-muted-foreground">No records yet.</td></tr>
              ) : (
                rows.map((r, i) => (
                  <tr key={r.id || i} className="border-b border-border last:border-0 hover:bg-muted/50" data-testid={`row-${active}-${i}`}>
                    {tab.cols.map(([k]) => (
                      <td key={k} className="max-w-[280px] truncate px-4 py-3 align-top">
                        {k === "status" && active === "leads" ? (
                          <select value={r.status || "new"} data-testid={`lead-status-${i}`}
                            onChange={(e) => updateStatus(r.id, e.target.value)}
                            className={`border px-2 py-1 text-xs font-medium focus:outline-none ${
                              (r.status || "new") === "won" ? "border-accent bg-accent text-accent-foreground"
                              : (r.status || "new") === "contacted" ? "border-border bg-muted text-foreground"
                              : "border-border bg-background text-muted-foreground"}`}>
                            <option value="new">New</option>
                            <option value="contacted">Contacted</option>
                            <option value="won">Won</option>
                          </select>
                        ) : k === "estimate" && r[k] ? `$${Number(r[k]).toLocaleString()}` : fmt(r[k])}
                      </td>
                    ))}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        <p className="mt-3 text-xs text-muted-foreground">{rows.length} record{rows.length === 1 ? "" : "s"}</p>
      </div>
    </div>
  );
}
